import { reformatDate, reformatDateForServer } from "@/helpers/workWithDates";

export const formatCandidateMeta = (candidate) => ({
    worksheet: 10,
    questCompletion: candidate.questCompletion ? candidate.questCompletion : null
});

export const formatCandidateForTableRecord = (candidate) => ({
    id: candidate.id,
    isShowRow: true,
    postCategorySpecialty: {
        position: {
            value: candidate.post_code,
        },
        value: candidate.post,
    },
    phone: {
        value: candidate.phone,
    },
    filial: {
        value: candidate.filial ? candidate.filial.filial_name : "",
        id: candidate.filial ? candidate.filial.id : "",
    },
    _meta: formatCandidateMeta(candidate),
    comment: {
        value: candidate.status.comment,
    },
    candidateComment: {
        value: candidate.comment,
    },
    created: {
        candidateState: {
            value: candidate.state,
            scheduledDateTime: {
                date: candidate.statusDate ? candidate.statusDate : "",
                time: candidate.statusTime ? candidate.statusTime : "",
            },
        },
        value: candidate.created,
    },
    fullName: {
        candidate: {
            id: candidate.id,
        },
        value: candidate.fio,
    },
    source: {
        source: {
            value: candidate.source.name,
            id: candidate.source.id,
        },
        value: candidate.source.name,
    },
    dateOfStatusChange: {
        candidateId: candidate.id,
        candidateStatus: {
            value: candidate.status.id,
            date: reformatDate(candidate.dateOfStatusChange),
            state: candidate.state,
            scheduledDate:
                candidate.statusDate && candidate.statusTime
                    ? `${candidate.statusDate}\xa0${candidate.statusTime}`
                    : "",
        },
        value: candidate.status.name,
    },
    invitedBy: {
        candidateReferral: {
            id: candidate.invitedBy ? candidate.invitedBy.id : "",
        },
        value: candidate.invitedBy ? candidate.invitedBy.name : "",
    },
    citizenship: {
        value: candidate.citizenship,
    },
    address: {
        value: candidate.address,
    },
    experience: {
        value: candidate.experience,
    },
    accountable: {
        value: candidate.accountable ? candidate.accountable.name : "",
        id: candidate.accountable ? candidate.accountable.id : "",
        avatar: candidate.accountable ? candidate.accountable.avatar : "",
    },
});

const columnStringFilterValues = {
    fullName: {
        1: "fio",
        2: "fio_like",
    },
    phone: {
        1: "phone",
        2: "phone_like",
    },
    address: {
        1: "address",
        2: "address_like",
    },
    invitedBy: {
        1: "invitedBy",
        2: "invitedBy_like",
    },
};

const columnDateFiltersValues = {
    created: {
        date: (date) => `created_from=${reformatDateForServer(date)}&created_before=${reformatDateForServer(date)}`,
        before: (date) => `created_before=${reformatDateForServer(date)}`,
        after: (date) => `created_from=${reformatDateForServer(date)}`,
        range: (date) => {
            const [start, end] = date.split(",");
            return `created_from=${reformatDateForServer(start)}&created_before=${reformatDateForServer(end)}`;
        },
    },
};

const columnsDictionary = {
    postCategorySpecialty: "post",
    created: "created",
    posts: "post",
    fullName: "fio",
    phone: "phone",
    invitedBy: "invitedBy",
    address: "address",
    experience: "experience",
    dateOfStatusChange: "status",
    source: "source",
    filial: "filial",
    citizenship: "citizenship",
};

const getColumnQueriedFilters = (columnFilters) => {
    const queryParams = [];

    Object.entries(columnFilters).forEach(([columnId, column]) => {
        if (column.type === "string") {
            const query = columnStringFilterValues[columnId][column.searchType];
            queryParams.push(`${query}=${column.value}`);
        }
        if (column.type === "list") {
            const columnQueryId = columnsDictionary[columnId];
            column.value.split(",").forEach((value) => {
                queryParams.push(`${columnQueryId}[]=${value}`);
            });
            if (column.searchType === "2") {
                queryParams.push(`excl${columnQueryId.charAt(0).toUpperCase() + columnQueryId.slice(1)}=1`);
            }
        }
        if (column.type === "date") {
            queryParams.push(columnDateFiltersValues[columnId][column.searchType](column.value));
        }
    });

    return queryParams;
};

export const getQueryForGlobalCandidatesFilters = (filters) => {
    const queryParams = [];

    const {
        candidateStatuses,
        candidateStatusesExcept,
        citizenships,
        citizenshipsExcept,
        filials,
        filialsExcept,
        positions,
        positionsExcept,
        referral,
        status,
        worksheetFillness,
        nameOrPhone,
        responsibleUsers,
    } = filters;

    if (candidateStatuses && candidateStatuses.length) {
        candidateStatuses.forEach((status) => {
            queryParams.push(`status[]=${status.value}`);
        });
        if (candidateStatusesExcept) {
            queryParams.push("exclStatus=1");
        }
    }
    if (citizenships && citizenships.length) {
        citizenships.forEach((citizenship) => {
            queryParams.push(`citizenship[]=${citizenship.value}`);
        });
        if (citizenshipsExcept) {
            queryParams.push("exclCitizenship=1");
        }
    }
    if (filials && filials.length) {
        filials.forEach((filial) => {
            queryParams.push(`filial[]=${filial.value}`);
        });
        if (filialsExcept) {
            queryParams.push("exclFilial=1");
        }
    }
    if (positions && positions.length) {
        positions.forEach((position) => {
            queryParams.push(`post[]=${position.value}`);
        });
        if (positionsExcept) {
            queryParams.push("exclPost=1");
        }
    }
    if (referral) {
        queryParams.push(`invitedBy_id=${referral.value}`);
    }
    if (status === "closed") {
        queryParams.push("closed=1");
    }
    if (status === "all") {
        queryParams.push("closed=0");
    }
    if (worksheetFillness && worksheetFillness.value) {
        queryParams.push(`questCompletion=${worksheetFillness.value}`);
    }
    if (nameOrPhone) {
        queryParams.push(`fioPhone=${nameOrPhone}`);
    }
    if (responsibleUsers && responsibleUsers.length) {
        responsibleUsers.forEach((user) => {
            queryParams.push(`accountable[]=${user === "default" ? null : user}`);
        });
    }

    return queryParams;
};

export const getQueryParamsForFetchingCandidates = (filters) => {
    const page = filters.page || 1;
    const sort = `${filters.sortingData.sortType === "desc" ? "-" : ""}${columnsDictionary[filters.sortingData.sortBy] === "status"
        ? "status_id"
        : columnsDictionary[filters.sortingData.sortBy]
        }`;

    const { columnFilters, isCanbanMode, positionToInclude } = filters;

    let queryParams = [`page=${page}`, `sort=${sort}`];

    if (positionToInclude) {
        queryParams.push(`canbanPost=${positionToInclude.value}`);
    }

    queryParams = queryParams.concat(getQueryForGlobalCandidatesFilters(filters));

    if (isCanbanMode) {
        queryParams.push("for_canban=1");
    }

    if (columnFilters && Object.keys(columnFilters).length) {
        const columnQueriedFilters = getColumnQueriedFilters(columnFilters);
        queryParams = queryParams.concat(columnQueriedFilters);
    }

    return queryParams;
};

export const getFormDataForStatusChange = (formValues) => {
    const formData = new FormData();

    const {
        candidateStatus,
        candidateEmail,
        comment,
        date,
        time,
        shouldCreateServiceUser,
        accountable,
        rejectionReason,
        anotherRejectionReason,
        position,
        mentor,
        rank,
        filial,
    } = formValues;

    formData.append("status_id", candidateStatus.value);

    if (comment) {
        formData.append("statusComment", comment);
    }
    if (date) {
        formData.append("status_date", date);
    }
    if (time) {
        formData.append("status_time", time);
    }
    if (shouldCreateServiceUser) {
        formData.append("createUser", "1");
    }
    if (accountable) {
        formData.append("accountable_id", accountable.value);
    }
    if (candidateEmail) {
        formData.append("email", candidateEmail);
    }
    if (rejectionReason) {
        formData.append("reason_id", rejectionReason.value);
    }
    if (anotherRejectionReason) {
        formData.append("reasonText", anotherRejectionReason);
    }
    if (position) {
        formData.append("post_code", position.value);
    }
    if (mentor) {
        formData.append("mentor_id", mentor.value);
    }
    if (filial) {
        formData.append("filial_id", filial.value);
    }
    if (rank) {
        formData.append("category_id", rank.value);
    }

    return formData;
};
